// components/FuturesPage/FuturesPage.jsx

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useLocalSearch from "../../hooks/useLocalSearch";
import useFutures from "../../hooks/useFutures";
import { IoMdTrendingUp, IoMdTrendingDown } from "react-icons/io";
import { FaSpinner } from "react-icons/fa6";
import { FiRefreshCw } from "react-icons/fi";
import { CiSearch } from "react-icons/ci";
import useLivePrices from "../../hooks/useLivePrices";
import "./FuturesPage.css";

const NSE_INDICES = ["NIFTY", "BANKNIFTY", "FINNIFTY", "MIDCPNIFTY"];

const FuturesPage = () => {
  const navigate = useNavigate();
  const {
    type,
    symbol: paramSymbol,
    securityId: paramSecurityId,
  } = useParams();

  const {
    query: localSearch,
    setQuery: setLocalSearch,
    equityResults: localEquityResults,
    loading: localSearchLoading,
    clearResults: clearLocalResults,
  } = useLocalSearch();

  const {
    futuresData,
    futuresLoading,
    futuresError,
    getFutures,
    getFuturesExpiry,
  } = useFutures();
  const { prices: livePrices, isLive, subscribeSymbols } = useLivePrices();

  const [activeSymbol, setActiveSymbol] = useState("");
  const [activeSecurityId, setActiveSecurityId] = useState("");
  const [selectedIndex, setSelectedIndex] = useState("");
  const [lastFetchType, setLastFetchType] = useState(null);
  const [lastUpdated, setLastUpdated] = useState("");
  const [selectDropdown, setSelectDropdown] = useState(false);

  // Format timestamp
  const formatTimestamp = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = monthNames[now.getMonth()];
    const year = now.getFullYear();
    const time = now.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    return `${day}-${month}-${year} ${time} IST`;
  };

  // Load from URL params on mount
  useEffect(() => {
    if (paramSymbol && paramSecurityId) {
      setActiveSymbol(paramSymbol.toUpperCase());
      setActiveSecurityId(paramSecurityId);
      setLastFetchType(
        paramSymbol === paramSymbol.toUpperCase() ? "symbol" : "index",
      );
      getFutures(paramSecurityId);
    } else if (!activeSymbol) {
      // Default: load NIFTY
      setSelectedIndex("NIFTY");
      setActiveSymbol("NIFTY");
      setLastFetchType("index");
      setSelectDropdown(true);
      // For NIFTY, security_id is 13
      getFutures("13");
    }
  }, [paramSymbol, paramSecurityId]);

  // Update timestamp when data arrives
  useEffect(() => {
    if (futuresData) {
      setLastUpdated(formatTimestamp());
    }
  }, [futuresData]);

  // This already handles both:
  useEffect(() => {
    if (activeSymbol) {
      subscribeSymbols([activeSymbol]); // "NIFTY" or "TCS" — both work
    }
  }, [activeSymbol]);

  const live = livePrices[activeSymbol];
  const liveSpotPrice = live?.ltp || futuresData?.spotPrice;

  // Handle index select
  const handleIndexSelect = (e) => {
    const indexSymbol = e.target.value;
    if (!indexSymbol) return;

    setSelectedIndex(indexSymbol);
    setActiveSymbol(indexSymbol);
    subscribeSymbols([indexSymbol]);
    setLastFetchType("index");
    setSelectDropdown(true);
    clearLocalResults();

    // Map index names to known security IDs
    const indexMap = {
      NIFTY: "13",
      BANKNIFTY: "25",
      FINNIFTY: "27",
      MIDCPNIFTY: "442",
    };
    const secId = indexMap[indexSymbol];
    if (secId) {
      setActiveSecurityId(secId);
      //   getFutures(secId);
      getFutures(secId, { type: "INDEX" });
    }
  };

  // Handle stock search select
  const handleStockSelect = async (item) => {
    const selectedSymbol =
      item?.underlyingSymbol || item?.underlying_symbol || item?.symbol;
    const selectedSecurityId =
      item?.securityId || item?.security_id || item?.underlyingId;

    if (!selectedSymbol || !selectedSecurityId) return;

    const formatted = selectedSymbol.trim().toUpperCase();

    setActiveSymbol(formatted);
    setActiveSecurityId(String(selectedSecurityId));
    setLastFetchType("symbol");
    subscribeSymbols([formatted]);
    setSelectedIndex("");
    setSelectDropdown(false);
    clearLocalResults();

    await getFutures(selectedSecurityId, { type: "STOCK" });
  };

  // Handle refresh
  const handleRefresh = async () => {
    if (!activeSecurityId) return;
    await getFutures(activeSecurityId);
    setLastUpdated(formatTimestamp());
  };

  const contracts = futuresData?.contracts || [];
  const spotPrice = futuresData?.spotPrice;

  const formatNumber = (num) => {
    if (num == null) return "—";
    return Number(num).toLocaleString("en-IN");
  };

  const formatVolume = (vol) => {
    if (vol == null) return "—";
    const num = Number(vol);
    if (num >= 10000000) return `${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `${(num / 100000).toFixed(2)} L`;
    return num.toLocaleString("en-IN");
  };

  return (
    <div className="futures-page">
      {/* Back button */}
      <div className="futures-page-topbar">
        <button
          className="rec-btn trendpro-back-btn"
          onClick={() => navigate(-1)}
        >
          Back
        </button>
      </div>

      {/* Search bar — same layout as Option Chain */}
      <div className="option-chain">
        <div className="option-menu nse-layout">
          <div className="option-chain-head">
            {/* Index dropdown */}
            <div className="select-option dropdown">
              <label>Index Futures</label>
              <select onChange={handleIndexSelect} value={selectedIndex}>
                <option value="">Select Index</option>
                {NSE_INDICES.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            <span className="select-span">OR</span>

            {/* Stock search */}
            <div className="search-option dropdown">
              <label>Search Stock</label>
              <div
                className="search-option-input"
                style={{
                  borderRadius: "12px",
                  background: "white",
                  display: "flex",
                  alignItems: "center",
                  padding: "0px 10px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4)",
                  position: "relative",
                }}
              >
                <input
                  type="text"
                  placeholder="Search Symbol"
                  value={localSearch}
                  onChange={(e) => setLocalSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") clearLocalResults();
                  }}
                  className="search-option-chain"
                  style={{
                    padding: "14px 15px",
                    borderRadius: "20px",
                    border: "none",
                    outline: "none",
                  }}
                />
                <span className="search-span" style={{ cursor: "pointer" }}>
                  <CiSearch />
                </span>

                {localSearchLoading && (
                  <span style={{ fontSize: "12px", marginLeft: "8px" }}>
                    Searching...
                  </span>
                )}

                {localEquityResults?.length > 0 && (
                  <div className="search-optionpage-dropdown">
                    <div className="search-section-title">Stocks</div>
                    {localEquityResults.map((item, index) => {
                      const itemSymbol = item?.underlyingSymbol || item?.symbol;
                      const itemSecurityId =
                        item?.securityId || item?.security_id;
                      return (
                        <div
                          key={itemSecurityId || `${itemSymbol}-${index}`}
                          className="search-option-dropdown-item"
                          onClick={() => handleStockSelect(item)}
                        >
                          <div className="dropdown-symbol">{itemSymbol}</div>
                          <div className="dropdown-company">
                            {item?.displayName ||
                              item?.company ||
                              item?.companyName}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loading */}
      {futuresLoading && (
        <p
          style={{
            fontSize: "1rem",
            fontWeight: "700",
            display: "flex",
            gap: "1rem",
          }}
        >
          Fetching futures data...
          <span style={{ paddingLeft: "10px", marginTop: "5px" }}>
            <FaSpinner />
          </span>
        </p>
      )}

      {/* Error */}
      {futuresError && !futuresLoading && (
        <div className="option-error">{futuresError}</div>
      )}

      {/* Data */}
      {!futuresLoading && futuresData && contracts.length > 0 && (
        <div>
          {/* Last updated */}
          <div
            className="last-updated-head"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontSize: "13px",
              color: "#6b7280",
            }}
          >
            {lastUpdated && <span>As on {lastUpdated}</span>}
            <FiRefreshCw
              size={16}
              onClick={handleRefresh}
              style={{ cursor: "pointer", transition: "0.2s" }}
              onMouseOver={(e) => (e.target.style.color = "#2fd18a")}
              onMouseOut={(e) => (e.target.style.color = "#6b7280")}
            />
          </div>

          {/* Summary bar */}
          <div
            className="grid-1"
            style={{
              gap: "10px",
              textAlign: "left",
              marginTop: "10px",
              boxShadow: "rgba(0, 0, 0, 0.4) 0px 4px 12px",
            }}
          >
            <p>
              <span>
                {selectDropdown ? "Contract" : futuresData.displayName}
                <span className="stock-tag" style={{ width: "max-content" }}>
                  {futuresData.symbol}
                </span>
              </span>
            </p>
            <p>
              Spot LTP
              <span
                style={{
                  fontWeight: "700",
                  backgroundColor: "#e6f0ff",
                  color: "#0062ff",
                  padding: "4px 10px",
                  borderRadius: "8px",
                }}
              >
                {liveSpotPrice ? `₹${formatNumber(liveSpotPrice)}` : "—"}
              </span>
            </p>
            <p>
              Type
              <span
                style={{
                  fontWeight: "700",
                  background: "#ede9fe",
                  color: "#7c3aed",
                  padding: "4px 10px",
                  borderRadius: "8px",
                }}
              >
                {futuresData.type === "INDEX"
                  ? "Index Futures"
                  : "Stock Futures"}
              </span>
            </p>
            <p>
              Lot Size
              <span
                style={{
                  fontWeight: "700",
                  background: "#d4f7e6",
                  color: "#008A3E",
                  padding: "4px 10px",
                  borderRadius: "8px",
                }}
              >
                {contracts[0]?.lotSize || "—"}
              </span>
            </p>
          </div>

          {/* Futures Table */}
          <div
            className="stock-table-container"
            style={{
              boxShadow: "rgba(0, 0, 0, 0.4) 0px 4px 12px",
              overflowX: "auto",
              marginTop: "20px",
            }}
          >
            <table
              className="stock-table"
              style={{ width: "100%", borderCollapse: "collapse" }}
            >
              <thead>
                <tr>
                  <th>Expiry</th>
                  <th>Open</th>
                  <th>High</th>
                  <th>Low</th>
                  <th>Close</th>
                  <th>LTP</th>
                  <th>Chg</th>
                  <th>%Chg</th>
                  <th>Volume</th>
                  <th>OI</th>
                  {spotPrice && <th>Premium</th>}
                </tr>
              </thead>
              <tbody>
                {contracts.map((c) => {
                  const isPos = c.change > 0;
                  const isNeg = c.change < 0;
                  const premium = spotPrice && c.ltp ? c.ltp - spotPrice : null;
                  const premiumPct =
                    spotPrice && premium
                      ? ((premium / spotPrice) * 100).toFixed(2)
                      : null;

                  return (
                    <tr key={c.securityId}>
                      <td>
                        <div
                          style={{ display: "flex", flexDirection: "column" }}
                        >
                          <span style={{ fontWeight: 600 }}>
                            {c.expiryDate}
                          </span>
                          <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                            {c.displayName}
                          </span>
                        </div>
                      </td>
                      <td>{c.open ? `₹${formatNumber(c.open)}` : "—"}</td>
                      <td>{c.high ? `₹${formatNumber(c.high)}` : "—"}</td>
                      <td>{c.low ? `₹${formatNumber(c.low)}` : "—"}</td>
                      <td>{c.close ? `₹${formatNumber(c.close)}` : "—"}</td>
                      <td style={{ fontWeight: 700 }}>
                        {c.ltp ? `₹${formatNumber(c.ltp)}` : "—"}
                      </td>
                      <td
                        style={{
                          color: isPos ? "#008A3E" : isNeg ? "#C53030" : "#999",
                          fontWeight: 600,
                        }}
                      >
                        {c.change != null ? (
                          <>
                            {isPos ? "+" : ""}
                            {c.change}
                            {isPos ? (
                              <IoMdTrendingUp style={{ marginLeft: 4 }} />
                            ) : isNeg ? (
                              <IoMdTrendingDown style={{ marginLeft: 4 }} />
                            ) : null}
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td
                        style={{
                          color: isPos ? "#008A3E" : isNeg ? "#C53030" : "#999",
                          fontWeight: 600,
                        }}
                      >
                        {c.changePercent != null
                          ? `${isPos ? "+" : ""}${c.changePercent}%`
                          : "—"}
                      </td>
                      <td>{formatVolume(c.volume)}</td>
                      <td>{formatVolume(c.oi)}</td>
                      {spotPrice && (
                        <td
                          style={{
                            color: premium >= 0 ? "#008A3E" : "#C53030",
                            fontWeight: 600,
                          }}
                        >
                          {premium != null ? (
                            <>
                              {premium >= 0 ? "+" : ""}
                              {premium.toFixed(2)}
                              <span style={{ opacity: 0.7, marginLeft: 4 }}>
                                ({premiumPct}%)
                              </span>
                            </>
                          ) : (
                            "—"
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Premium Cards */}
          {/* {liveSpotPrice && contracts.some((c) => c.ltp) && (
            <div
              className="futures-premium-section"
              style={{ marginTop: "20px" }}
            >
              <h4>
                Futures Premium / Discount to Spot ₹{formatNumber(liveSpotPrice)}
              </h4>
              <div className="futures-premium-grid">
                {contracts.map((c) => {
                  if (!c.ltp || !spotPrice) return null;
                  const premium = c.ltp - spotPrice;
                  const premiumPct = ((premium / spotPrice) * 100).toFixed(2);
                  const isPos = premium >= 0;

                  return (
                    <div className="futures-premium-card" key={c.securityId}>
                      <span className="futures-premium-label">
                        {c.displayName}
                      </span>
                      <span
                        className={`futures-premium-value ${isPos ? "futures-green" : "futures-red"}`}
                      >
                        {isPos ? "+" : ""}
                        {premium.toFixed(2)} ({isPos ? "+" : ""}
                        {premiumPct}%)
                      </span>
                      <span className="futures-premium-tag">
                        {isPos ? "Premium" : "Discount"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )} */}
          {liveSpotPrice && contracts.some((c) => c.ltp) && (
            <div
              className="futures-premium-section"
              style={{ marginTop: "20px" }}
            >
              <h4>
                Futures Premium / Discount to Spot ₹
                {formatNumber(liveSpotPrice)}
              </h4>
              <div className="futures-premium-grid">
                {contracts.map((c) => {
                  if (!c.ltp || !liveSpotPrice) return null; // ← changed
                  const premium = c.ltp - liveSpotPrice; // ← changed
                  const premiumPct = ((premium / liveSpotPrice) * 100).toFixed(
                    2,
                  ); // ← changed
                  const isPos = premium >= 0;

                  return (
                    <div className="futures-premium-card" key={c.securityId}>
                      <span className="futures-premium-label">
                        {c.displayName}
                      </span>
                      <span
                        className={`futures-premium-value ${isPos ? "futures-green" : "futures-red"}`}
                      >
                        {isPos ? "+" : ""}
                        {premium.toFixed(2)} ({isPos ? "+" : ""}
                        {premiumPct}%)
                      </span>
                      <span className="futures-premium-tag">
                        {isPos ? "Premium" : "Discount"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Links to Option Chain & Max Pain */}
          <div style={{ display: "flex", gap: "12px", marginTop: "20px" }}>
            <button
              className="rec-btn trendpro-back-btn"
              onClick={() =>
                navigate(
                  `/optionpage/${futuresData.type}/${futuresData.symbol}/${activeSecurityId}`,
                )
              }
            >
              View Option Chain →
            </button>
            <button
              className="rec-btn trendpro-back-btn"
              onClick={() =>
                navigate(
                  `/maxpain/${futuresData.type}/${futuresData.symbol}/${activeSecurityId}`,
                )
              }
            >
              View Max Pain →
            </button>
          </div>
        </div>
      )}

      {/* No contracts */}
      {!futuresLoading && futuresData && contracts.length === 0 && (
        <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
          No active futures contracts for {activeSymbol}
        </div>
      )}
    </div>
  );
};

export default FuturesPage;
